<script lang="ts">
// App recede — the Avgrund depth push. Refcounted at module
// scope: several instances of this component can be mounted on one page
// (the connecting module stacks five), and the app must stay receded until
// the LAST open modal releases it.
let recedeCount = 0
let abyssTimer: ReturnType<typeof setTimeout> | undefined

// Stacked modals: only the top of the stack owns outside-click and Escape
// — one tap dismisses the overlay you tapped, never a cascade below it.
const modalStack: symbol[] = []
const RECEDE_CLASS = 'v-modal-recede-active'
const ABYSS_CLASS = 'v-modal-abyss-active'

// The abyss paints instantly (no fade): at scale 1 the card covers the
// viewport, so the color switch is occluded both directions — black is
// merely REVEALED as the card shrinks. On release the abyss must linger
// until the return transition finishes covering the viewport again, or
// the gutter flashes white mid-return. Reads the return-duration token
// so per-instance timing (e.g. RecedeLab) is honored.
function returnDurationMs(): number {
  const raw = getComputedStyle(document.body).getPropertyValue('--v-modal-recede-return-duration').trim()
  const parsed = /^([\d.]+)(s|ms)?$/.exec(raw)
  if (!parsed) return 350
  const value = parseFloat(parsed[1])
  return parsed[2] === 's' ? value * 1000 : value
}

function acquireRecede() {
  recedeCount += 1
  // A reopen during the exit linger re-owns the abyss — cancel its timer.
  if (abyssTimer) {
    clearTimeout(abyssTimer)
    abyssTimer = undefined
  }
  document.body.classList.add(RECEDE_CLASS, ABYSS_CLASS)
}

function releaseRecede() {
  recedeCount = Math.max(0, recedeCount - 1)
  if (recedeCount > 0) return
  // Recede off now so the card starts its return immediately; the abyss
  // stays behind it until the card has fully covered the viewport.
  document.body.classList.remove(RECEDE_CLASS)
  abyssTimer = setTimeout(() => {
    abyssTimer = undefined
    document.body.classList.remove(ABYSS_CLASS)
  }, returnDurationMs() + 50)
}
</script>

<script setup lang="ts">
import { onClickOutside, useMagicKeys } from '@vueuse/core'
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'
import { onUnmounted, ref, watch } from 'vue'
import { VCard, VIcon } from '@/modules/shared/components'

// Root is <Teleport>, so attribute fallthrough is unreliable — bind $attrs
// onto the overlay ourselves. Callers (and the Storybook motion lab) inject
// the motion tokens below through :style.
defineOptions({ inheritAttrs: false })

const props = defineProps({
  modalStore: {
    type: Object,
    required: true,
  },
  widthClass: {
    type: String,
    default: 'w-1/2',
  },
  // Optional leading glyph (VIcon name) floating over the soft halo —
  // the same mark treatment as the deals pipeline empty states.
  icon: {
    type: String,
    default: null,
  },
  // Accessible name for the dialog (aria-label). Every caller should
  // provide one — e.g. "Move Deal" — so screen readers announce it.
  label: {
    type: String,
    default: null,
  },
  // Bare: no VCard chrome — the slot paints its own surface (DealCard,
  // for one, is itself a VCard). The dialog itself becomes the scroller
  // (viewport-capped, overscroll-contained — tall content must never
  // extend past the fold, where the fixed wash makes it unreachable),
  // and its p-6 padding is a paint gutter so nothing on the card clips
  // against the scroll port — sized for a slot surface floating at
  // elevation-high, whose dominant shadow layers paint ~24px out
  // (8px offset + 16px blur); only the faintest 32px-blur tail clips,
  // imperceptible over the wash. The cap mirrors the raised dock (top-[40%],
  // matching v-modal-dialog's pb-[20vh] bias): max-h-[80vh] spans the space
  // above it, so a tall card's top cut lands at the viewport line.
  // The inner .v-modal__panel div carries only the fold motion; the
  // frosted paint rule is section-scoped, so a div skips it.
  bare: {
    type: Boolean,
    default: false,
  },
  // Avgrund depth push (see the recede CSS below): scales/blurs the whole
  // app behind the overlay while this modal is open. Opt-in — the effect
  // is currently parked (brand call), so no call site passes true; the
  // machinery, layout hooks (.v-recede-surface), and RecedeLab remain
  // wired for the day it returns. Flip-on is a one-prop change.
  recede: {
    type: Boolean,
    default: false,
  },
})

const target = ref(null)

// this instance's slot in the shared open-modals stack
const stackId = Symbol('v-pop-up-modal')
const isTopModal = () => modalStack[modalStack.length - 1] === stackId
const releaseStack = () => {
  const index = modalStack.indexOf(stackId)
  if (index !== -1) modalStack.splice(index, 1)
}

// Focus trap owns the keyboard story: Tab cycles inside the panel, initial
// focus lands on the first tabbable (the Cancel button in our confirm
// modals), and focus returns to the opener on close. Escape and
// outside-click stay ours (below) — the trap is told to ignore both.
const { activate, deactivate } = useFocusTrap(target, {
  escapeDeactivates: false,
  clickOutsideDeactivates: false,
  allowOutsideClick: true,
  fallbackFocus: () => target.value as HTMLElement,
})

watch(
  () => props.modalStore.state.active,
  (active, wasActive) => {
    // v-if mounts the panel this tick; activate once it exists. immediate
    // catches a store that is already active at setup; skip the deactivate
    // on that first run — there is no trap to release yet.
    if (active) {
      // Guard: the frame can land after a same-frame close or unmount,
      // when no panel is connected — activating then throws inside the
      // focus-trap (fallbackFocus resolves to nothing) as an uncaught rAF
      // error.
      requestAnimationFrame(() => {
        if (props.modalStore.state.active && target.value?.isConnected) activate()
      })
      modalStack.push(stackId)
      if (props.recede) acquireRecede()
    } else if (wasActive) {
      releaseStack()
      deactivate()
      if (props.recede) releaseRecede()
    }
  },
  { immediate: true },
)

onUnmounted(() => {
  if (props.modalStore.state.active) {
    releaseStack()
    deactivate()
    if (props.recede) releaseRecede()
  }
})

onClickOutside(target, () => {
  if (isTopModal()) props.modalStore.close()
})

const { escape } = useMagicKeys()
watch(escape, (pressed) => {
  if (pressed && props.modalStore.state.active && isTopModal()) props.modalStore.close()
})
</script>

<template>
  <Teleport to="body">
    <Transition name="v-modal">
      <!-- data-menu-portal on the wash: ActionsMenu panels triggered inside
           the modal teleport into this fixed container (see resolveMenuTarget
           in actions-menu.vue) — the wash is the topmost plane, so they paint
           above the dialog instead of behind it in the app shell. -->
      <div
        v-if="modalStore.state.active"
        class="v-modal__wash fixed inset-0 top-1 z-[999999] bg-gradient-to-t from-white/0 via-gray-200/70 to-gray-400/90"
        data-menu-portal
        v-bind="$attrs"
      >
        <div class="v-modal__halo" aria-hidden="true"></div>
        <div
          ref="target"
          class="absolute left-1/2 top-[40%] -translate-x-1/2 -translate-y-1/2"
          :class="[widthClass, bare ? 'v-modal__scroller max-h-[80vh] overflow-y-auto overscroll-contain p-6' : '']"
          role="dialog"
          aria-modal="true"
          :aria-label="label || undefined"
        >
          <!-- Bare: the slot paints its own surface (DealCard, for one, is
               itself a VCard). The DIALOG scrolls — the whole container,
               not an inner wrapper — with p-6 as a paint gutter (the
               elevation-high slot surface paints ~24px of shadow): the inner
               motion wrapper stays overflow-visible so the card's shadow
               and its floating top controls never clip against the scroll
               port. The dialog parks at the raised center (top-[40%], the
               same optical position as v-modal-dialog) with max-h-[80vh]
               spanning the space above it. The frosted
               paint rule is section-scoped, so a div keeps the fold motion
               without painting. -->
          <div v-if="bare" class="v-modal__panel" data-testid="v-modal-panel">
            <slot></slot>
          </div>
          <VCard
            v-else
            elevation="high"
            class="v-modal__panel [--card-padding-x:2rem] [--card-padding-y:2rem]"
            data-testid="v-modal-panel"
          >
            <div class="v-modal__content">
              <div v-if="icon" class="v-modal__icon" aria-hidden="true">
                <div class="v-modal__icon-halo"></div>
                <VIcon :name="icon" class="v-modal__icon-glyph" />
              </div>
              <slot></slot>
            </div>
          </VCard>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style>
/* Modal motion. Unscoped because the panel teleports to <body> and slotted
   children carry their parent's scope id; every selector is namespaced
   under .v-modal (same pattern as actions-menu's scrollbar styles).

   Panel: the venture360.co nav-flyout fold, verbatim — hinged at top
   center, unfolding from rotateX(-30deg) with the easeOutQuint-like
   curve. The panel docks near the viewport top so the hinge attaches to
   the page frame: enter unfolds down from the edge, leave tucks up out
   of sight. The flyout animates nothing inside its panel; neither do we:
   content rides in with the fold. The panel re-mounts per open, so the
   motion replays every time.

   Motion tokens (CSS custom properties, overridable per instance via a
   style binding on the component; defaults are the site-true values):

   Geometry — the fold both directions shares one trio, with a separate
   tuck-* set for the leave so an exit can differ (e.g. tuck without
   re-folding); each tuck var falls back to its fold counterpart:
     --v-modal-fold-angle   (-30deg)   --v-modal-tuck-angle
     --v-modal-fold-rise    (-4px)     --v-modal-tuck-rise
     --v-modal-fold-scale   (0.92)     --v-modal-tuck-scale

   Timing — duration and easing per leg, enter and leave independent:
     --v-modal-fade-in-duration  (0.15s)   --v-modal-fade-out-duration  (0.15s)
     --v-modal-fade-in-easing    (ease-out) --v-modal-fade-out-easing   (ease-in)
     --v-modal-fold-in-duration  (0.2s)    --v-modal-fold-out-duration  (0.15s)
     --v-modal-enter-easing      (quint)   --v-modal-leave-easing       (ease-in) */

.v-modal__panel {
  transform-origin: top center;
  will-change: transform;
}

/* Bare-scroller scrollbar — the board column treatment, verbatim. Styling
   the webkit scrollbar opts the dialog out of macOS's overlay bars: the
   default overlay bar only exists mid-flick, so the scroll region reads as
   an invisible clip against the wash. A persistent 8px thumb makes the
   scroller — and the viewport-aligned cap — legible. */
.v-modal__scroller {
  scrollbar-width: thin;
  scrollbar-color: #d1d5db transparent;
}
.v-modal__scroller::-webkit-scrollbar {
  width: 8px;
}
.v-modal__scroller::-webkit-scrollbar-track {
  background: transparent;
}
.v-modal__scroller::-webkit-scrollbar-thumb {
  background-color: #d1d5db;
  border-radius: 8px;
}

/* Frosted-glass panel (marketing-site grammar): venture360.co's nav is
   rgba(29,52,71,0.6) + blur(8px), and its light-section .btn-glass is
   white at 0.95→0.85 + blur(8px). The panel's analog of .btn-glass —
   white at a slight transparency with a soft blur of what shows through
   (the wash, the halo, and — through the overlay's transparent bottom —
   the page). Tunable per instance:
     --v-modal-panel-alpha  (0.92;   marketing analog: 0.9)
     --v-modal-panel-blur   (8px;    marketing analog: 8px)
   The section-element selector (0,1,1) outspecifies VCard's bg-white
   utility (0,1,0) without !important. Note: any backdrop-filter value
   (even blur(0)) makes the element a stacking context — content is
   in-flow, so nothing shifts. */
section.v-modal__panel {
  background-color: rgb(255 255 255 / var(--v-modal-panel-alpha, 0.92));
  -webkit-backdrop-filter: blur(var(--v-modal-panel-blur, 8px));
  backdrop-filter: blur(var(--v-modal-panel-blur, 8px));
}

.v-modal-enter-active {
  transition: opacity var(--v-modal-fade-in-duration, 0.15s) var(--v-modal-fade-in-easing, ease-out);
}
.v-modal-enter-active .v-modal__panel {
  transition: transform var(--v-modal-fold-in-duration, 0.2s)
    var(--v-modal-enter-easing, cubic-bezier(0.22, 1, 0.36, 1));
}
.v-modal-enter-from {
  opacity: 0;
}
.v-modal-enter-from .v-modal__panel {
  transform: perspective(1000px) translateY(var(--v-modal-fold-rise, -4px)) rotateX(var(--v-modal-fold-angle, -30deg))
    scale(var(--v-modal-fold-scale, 0.92));
}

.v-modal-leave-active {
  transition: opacity var(--v-modal-fade-out-duration, 0.15s) var(--v-modal-fade-out-easing, ease-in);
}
.v-modal-leave-active .v-modal__panel {
  transition: transform var(--v-modal-fold-out-duration, 0.15s) var(--v-modal-leave-easing, cubic-bezier(0.4, 0, 1, 1));
}
.v-modal-leave-to {
  opacity: 0;
}
.v-modal-leave-to .v-modal__panel {
  transform: perspective(1000px) translateY(var(--v-modal-tuck-rise, var(--v-modal-fold-rise, -4px)))
    rotateX(var(--v-modal-tuck-angle, var(--v-modal-fold-angle, -30deg)))
    scale(var(--v-modal-tuck-scale, var(--v-modal-fold-scale, 0.92)));
}

.v-modal__icon {
  position: relative;
  display: flex;
  height: 5rem;
  width: 5rem;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
}
.v-modal__icon-halo {
  position: absolute;
  inset: -0.75rem;
  border-radius: 9999px;
  background: rgb(243 244 246 / 0.7);
  filter: blur(24px);
}
.v-modal__icon-glyph {
  position: relative;
  display: block;
  height: 2.25rem;
  width: 2.25rem;
  color: #d1d5db;
}

/* Whisper scrim — the uniform near-invisible layer UNDER the gradient:
   background-color paints beneath background-image, so the transparent
   parts of the wash show the scrim and the whole backdrop (not just the
   top strip) reads as lifted off the page. At 0.03 most users can't
   name why the page behind the modal feels distant — they just feel the
   separation. Tunable per instance:
     --v-modal-scrim-alpha  (0.03) */
.v-modal__wash {
  background-color: rgb(17 24 39 / var(--v-modal-scrim-alpha, 0.03));
}

/* Backdrop halo — the empty-state's blurred ellipse (v-empty-state.vue),
   anchored at the panel's hinge: an extra crown of tint where the fold
   originates, dissolving outward. The top-centered gradient above starts
   from the drawer modal's site-true tones, deepened (gray-200 via,
   gray-400/90 top) so the wash is actually visible on white pages — the
   site-true white/30 over a white page painted nothing. The overlay
   starts at top-1 (4px) so the app's gradient bar (VBar) stays visible
   above the wash — same convention as v-modal/v-modal-dialog. Tunable
   per instance through the same custom-prop pattern as the motion
   tokens:
     --v-modal-halo-alpha   (0.1)
     --v-modal-halo-width   (36rem)
     --v-modal-halo-height  (18rem)
     --v-modal-halo-blur    (40px)
   Geometry: the ellipse is half-cropped above the viewport, so only the
   bottom half shows — a crown between the viewport top and the panel, plus
   side arcs where the halo outspans the panel. Width sets horizontal reach
   (kept narrow — panel width plus a few rem of bleed per side — so the
   accent reads as a column of light at the hinge, not a full-width band);
   height sets crown depth. The 36:18 ratio (2:1) stays in the empty-state
   halo's glow family (2.29:1). */
.v-modal__halo {
  position: absolute;
  top: calc(var(--v-modal-halo-height, 18rem) / -2);
  left: 50%;
  width: var(--v-modal-halo-width, 36rem);
  height: var(--v-modal-halo-height, 18rem);
  transform: translateX(-50%);
  border-radius: 9999px;
  background: rgb(17 24 39 / var(--v-modal-halo-alpha, 0.1));
  filter: blur(var(--v-modal-halo-blur, 40px));
  pointer-events: none;
}

/* App recede — the Avgrund depth push (lab.hakim.se/avgrund), PARKED:
   opt-in via the `recede` prop, which no call site passes today, so
   these rules are inert. Everything below is fully wired and tested —
   the layouts' hook classes, the refcounted trigger, the labs — so
   re-enabling (or A/B-ing in RecedeLab) is a single prop. While a
   receding modal is open, the app surface under the overlay scales
   down, blurs slightly, rounds its corners, and sits on a near-black
   body so the revealed gutter reads as an abyss separating sheet from
   background. The modal itself is teleported to <body>, outside every
   layout, so it never inherits the scale (Avgrund counter-scaled its
   popup instead — same net effect, ours is cleaner).

   The recede target is the layouts' fixed inset-0 wrapper — the element
   that contains ALL app chrome: banners, gradient bar, impersonation
   chip, sidenav, and the app surface itself (TheLayout,
   ConnectionsLayout, DashboardLayout) — tagged with the v-recede-surface
   hook class; #storybook-root is the lab's stand-in. Receding #app
   itself does not work: it is a transparent in-flow shell (~zero-height,
   since its only in-flow child is the banners), so the surface paint is
   invisible and overflow: hidden clips the re-anchored fixed layout away
   entirely — the "pure black behind the modal" failure. The wrapper has
   none of these problems: it is viewport-boxed (50% 50% is always the
   visible center — no scroll compensation needed), and its fixed
   children (bar, banners, sidenav, scroll container) re-anchor to its
   own box, which IS the viewport box, so resting geometry is unchanged
   and the whole app retreats as one card.

   Mechanics: two body classes, refcounted across instances. The recede
   class drives the card's scale/blur/radius and is removed at close so
   the return starts immediately; the abyss class paints the body behind
   and lingers one return-duration longer (JS timer) so the gutter stays
   dark until the card covers the viewport — both color snaps are
   occluded at scale 1 (see the abyss rule below). The base transitions
   on the targets are inert where no target matches, and they must be
   unconditional — the return leg has to outlive both classes — so any
   other transform/filter on a target would animate; none exists today.

   Known trade-offs, accepted: body-teleported popovers (dropdowns,
   tooltips — anything in a <Teleport to="body">) do not recede; they
   float over the abyss while a modal is open. overflow: hidden +
   radius revert with the class, a corner-pop the 0.15s overlay fade
   covers. Blur repaints the whole surface; drop --v-modal-recede-blur
   to 0 if large viewports stutter.

   Recede tokens (set on <body> to reach the surface — the component's
   :style lands on the overlay, and vars inherit downward only).
   Avgrund-true is scale 0.9 / blur 2px / veil 0.5 over #222 with a
   noise texture; our defaults are the brand-tuned middle:
     --v-modal-recede-scale            (0.94;  Avgrund: 0.9)
     --v-modal-recede-blur             (2px;   Avgrund: 2px)
     --v-modal-recede-radius           (4px)
     --v-modal-recede-surface          (#fff — the receded page's paint)
     --v-modal-abyss-color             (#0b0b0c — the frame behind)
     --v-modal-recede-duration         (0.3s, ease-in-out-quad both legs —
     --v-modal-recede-return-duration   page, veil, and fold move as one) */
/* The abyss paints INSTANTLY (no transition) in both directions: at
   scale 1 the card covers the viewport, so the white→abyss snap on open
   and the abyss→white snap on close (delayed until the return covers,
   see releaseRecede) are both fully occluded — the abyss is never seen
   fading, only revealed by the shrinking/growing card, which is what
   sells "the app moved back" instead of "the background changed". */
body.v-modal-abyss-active {
  /* Avgrund textures this with a repeating noise PNG over #222 — swap
     point if we want a patterned frame: set background-image here and
     keep the color as its fallback. */
  background: var(--v-modal-abyss-color, #0b0b0c);
}

.v-recede-surface,
#storybook-root {
  transition:
    transform var(--v-modal-recede-return-duration, 0.3s)
      var(--v-modal-recede-easing, cubic-bezier(0.25, 0.46, 0.45, 0.94)),
    filter var(--v-modal-recede-return-duration, 0.3s)
      var(--v-modal-recede-easing, cubic-bezier(0.25, 0.46, 0.45, 0.94)),
    border-radius var(--v-modal-recede-return-duration, 0.3s)
      var(--v-modal-recede-easing, cubic-bezier(0.25, 0.46, 0.45, 0.94)),
    box-shadow var(--v-modal-recede-return-duration, 0.3s)
      var(--v-modal-recede-easing, cubic-bezier(0.25, 0.46, 0.45, 0.94));
}

body.v-modal-recede-active .v-recede-surface,
body.v-modal-recede-active #storybook-root {
  transform: scale(var(--v-modal-recede-scale, 0.94));
  filter: blur(var(--v-modal-recede-blur, 2px));
  border-radius: var(--v-modal-recede-radius, 4px);
  overflow: hidden;
  /* The receded surface already paints itself (bg-white in the layouts);
     the var is a per-instance override, not a patch for transparency. */
  background: var(--v-modal-recede-surface, #fff);
  box-shadow:
    0 0 0 1px rgb(0 0 0 / 0.35),
    0 2rem 4rem rgb(0 0 0 / 0.3);
  transition:
    transform var(--v-modal-recede-duration, 0.3s) var(--v-modal-recede-easing, cubic-bezier(0.25, 0.46, 0.45, 0.94)),
    filter var(--v-modal-recede-duration, 0.3s) var(--v-modal-recede-easing, cubic-bezier(0.25, 0.46, 0.45, 0.94)),
    border-radius var(--v-modal-recede-duration, 0.3s) ease-out,
    box-shadow var(--v-modal-recede-duration, 0.3s) ease-out;
  will-change: transform;
}

/* Chromium failure mode: a backdrop-filter element whose ancestor has
   filter/will-change (this card, while receded or returning) can stop
   painting the element entirely — subtree and all. The gradient bar and
   toasts live in that subtree, so backdrop-filters are neutralized
   card-wide for the whole recede + return window (keyed on the abyss
   class, which lingers past the card's return); the frosted looks return
   once the card settles at scale 1. Selector specificity (0,2,2)
   outranks Tailwind's backdrop-blur utility. */
body.v-modal-abyss-active .v-recede-surface *,
body.v-modal-abyss-active #storybook-root * {
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
}

@media (prefers-reduced-motion: reduce) {
  .v-modal-enter-active,
  .v-modal-leave-active,
  .v-modal-enter-active .v-modal__panel,
  .v-modal-leave-active .v-modal__panel {
    transition: none;
  }

  /* Recede is pure ornament — under reduced motion the class may toggle
     but nothing moves: no scale, no blur, no transition to run it. */
  .v-recede-surface,
  body.v-modal-recede-active .v-recede-surface,
  body.v-modal-recede-active #storybook-root {
    transform: none;
    filter: none;
    transition: none;
  }
}
</style>
